import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Statistic, Space, DatePicker, Spin, Alert, Modal } from 'antd';
import { 
  BarChartOutlined, 
  ShoppingCartOutlined, 
  HomeOutlined, 
  LogoutOutlined,
  FileTextOutlined,
  InboxOutlined,
  DollarOutlined,
  AppstoreOutlined,
  TeamOutlined,
  WarningOutlined,
  SwapOutlined,
  MenuOutlined,
  FilePdfOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import dayjs from 'dayjs';
import { getHeaders } from '../utility/getHeader';
import Layout from '../layout/Layout';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const WarehouseStoreManagerDashboard = () => {
  const navigate = useNavigate();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [dailyMovement, setDailyMovement] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [inventoryMenuVisible, setInventoryMenuVisible] = useState(false);
  const [sponsorPurchases, setSponsorPurchases] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [loadingSponsor, setLoadingSponsor] = useState(false);
  const sponsorChartRef = useRef(null);

  // Get user information from localStorage/sessionStorage
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Manager';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  // Check if user is authenticated and is a warehouse store manager
  const isWarehouseStoreManager = userType && (userType === 'warehousestoremanager' || userType === 'warehouse_store_manager' || userType === 'wstoremanager');
  const isAuthenticated = !!token;

  const fetchDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      const [summaryRes, categoryRes, movementRes] = await Promise.all([
        axios.get('/warehousemanager/summary', {
          params: { from: startDate, to: endDate },
          ...getHeaders()
        }),
        axios.get('/warehousemanager/category-summary', {
          params: { from: startDate, to: endDate },
          ...getHeaders()
        }),
        axios.get('/warehousemanager/daily-movement', {
          params: { from: startDate, to: endDate },
          ...getHeaders()
        })
      ]);

      setDashboardData(summaryRes.data.data);
      setCategoryData(categoryRes.data.data);
      setDailyMovement(movementRes.data.data);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast.error('Failed to load dashboard data: ' + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchSponsorPurchases = React.useCallback(async () => {
    try {
      setLoadingSponsor(true);
      const month = selectedMonth.format('MM');
      const year = selectedMonth.format('YYYY');

      const response = await axios.get('/warehousemanager/sponsor-monthly-purchase', {
        params: { month, year },
        ...getHeaders()
      });

      setSponsorPurchases(response.data);
      
    } catch (err) {
      console.error('Error fetching sponsor purchases:', err);
      toast.error('Failed to load sponsor purchase data: ' + (err.response?.data?.msg || err.message));
    } finally {
      setLoadingSponsor(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    // Check authentication
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user is warehouse store manager
    if (!isWarehouseStoreManager) {
      setAccessDenied(true);
      return;
    }

    // Fetch dashboard data
    fetchDashboardData();
  }, [isAuthenticated, isWarehouseStoreManager, navigate, fetchDashboardData]);

  useEffect(() => {
    if (isAuthenticated && isWarehouseStoreManager && dateRange && dateRange.length === 2) {
      fetchDashboardData();
    }
  }, [dateRange, isAuthenticated, isWarehouseStoreManager, fetchDashboardData]);

  useEffect(() => {
    if (isAuthenticated && isWarehouseStoreManager && selectedMonth) {
      fetchSponsorPurchases();
    }
  }, [selectedMonth, isAuthenticated, isWarehouseStoreManager, fetchSponsorPurchases]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Logged out successfully!');
    navigate('/login');
  };

  // Chart configurations
  const getCategoryChartData = () => {
    if (!categoryData || categoryData.length === 0) return null;

    return {
      labels: categoryData.map(cat => cat.category),
      datasets: [
        {
          label: 'Current Stock',
          data: categoryData.map(cat => cat.current_stock),
          backgroundColor: 'rgba(139, 69, 19, 0.6)',
          borderColor: 'rgba(139, 69, 19, 1)',
          borderWidth: 1
        },
        {
          label: 'Total Sold',
          data: categoryData.map(cat => cat.total_sold),
          backgroundColor: 'rgba(52, 168, 83, 0.6)',
          borderColor: 'rgba(52, 168, 83, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const getDailyMovementChartData = () => {
    if (!dailyMovement || dailyMovement.length === 0) return null;

    return {
      labels: dailyMovement.map(day => dayjs(day.date).format('MMM DD')),
      datasets: [
        {
          label: 'Items Received',
          data: dailyMovement.map(day => day.items_received),
          borderColor: 'rgba(139, 69, 19, 1)',
          backgroundColor: 'rgba(139, 69, 19, 0.2)',
          tension: 0.4
        },
        {
          label: 'Items Sold',
          data: dailyMovement.map(day => day.items_sold),
          borderColor: 'rgba(52, 168, 83, 1)',
          backgroundColor: 'rgba(52, 168, 83, 0.2)',
          tension: 0.4
        }
      ]
    };
  };

  const getStockDistributionData = () => {
    if (!categoryData || categoryData.length === 0) return null;

    return {
      labels: categoryData.map(cat => cat.category),
      datasets: [{
        data: categoryData.map(cat => cat.current_stock),
        backgroundColor: [
          'rgba(139, 69, 19, 0.8)',
          'rgba(205, 133, 63, 0.8)',
          'rgba(210, 105, 30, 0.8)',
          'rgba(160, 82, 45, 0.8)',
          'rgba(139, 90, 43, 0.8)',
          'rgba(101, 67, 33, 0.8)'
        ],
        borderColor: '#fff',
        borderWidth: 2
      }]
    };
  };

  const getSponsorPurchaseChartData = () => {
    if (!sponsorPurchases || !sponsorPurchases.data || sponsorPurchases.data.length === 0) return null;

    return {
      labels: sponsorPurchases.data.map(sponsor => sponsor.sponsor_name),
      datasets: [
        {
          label: 'Total Purchase Amount (฿)',
          data: sponsorPurchases.data.map(sponsor => parseFloat(sponsor.total_purchase_amount || 0)),
          backgroundColor: 'rgba(139, 69, 19, 0.6)',
          borderColor: 'rgba(139, 69, 19, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const exportSponsorDataToPDF = () => {
    if (!sponsorPurchases || !sponsorPurchases.data || sponsorPurchases.data.length === 0) {
      toast.warning('No sponsor data to export');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header - Compact
    doc.setFillColor(139, 69, 19);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Sponsor Purchase Report', pageWidth / 2, 10, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Period: ${sponsorPurchases.period} | Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, pageWidth / 2, 18, { align: 'center' });

    // Summary Statistics
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    const totalSponsors = sponsorPurchases.count;
    const totalAmount = sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_purchase_amount || 0), 0);
    const totalOrders = sponsorPurchases.data.reduce((sum, s) => sum + parseInt(s.purchase_orders || 0), 0);
    const totalItems = sponsorPurchases.data.reduce((sum, s) => sum + parseInt(s.unique_items || 0), 0);
    const totalQty = sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_quantity || 0), 0);

    doc.setFont(undefined, 'bold');
    doc.text('Summary:', 14, 33);
    doc.setFont(undefined, 'normal');
    doc.text(`Total Sponsors: ${totalSponsors}`, 14, 40);
    doc.text(`Total Purchase Orders: ${totalOrders}`, 14, 46);
    doc.text(`Total Unique Items: ${totalItems}`, 14, 52);
    doc.text(`Total Quantity: ${totalQty.toFixed(2)}`, 14, 58);
    doc.setFont(undefined, 'bold');
    doc.text(`Total Purchase Amount: THB ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 14, 64);

    // Table
    const tableData = sponsorPurchases.data.map((sponsor, index) => [
      index + 1,
      sponsor.sponsor_name,
      sponsor.purchase_orders,
      sponsor.unique_items,
      parseFloat(sponsor.total_quantity || 0).toFixed(2),
      'THB ' + parseFloat(sponsor.total_purchase_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    ]);

    // Add total row
    tableData.push([
      { content: 'TOTAL', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [230, 247, 255] } },
      totalOrders,
      totalItems,
      totalQty.toFixed(2),
      'THB ' + totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    ]);

    doc.autoTable({
      startY: 72,
      head: [['#', 'Sponsor Name', 'POs', 'Items', 'Quantity', 'Purchase Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [139, 69, 19],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { halign: 'left', cellWidth: 60 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'center', cellWidth: 20 },
        4: { halign: 'center', cellWidth: 30 },
        5: { halign: 'left', cellWidth: 45, fontStyle: 'bold' }
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249]
      }
    });

    // Add chart image after table if available
    if (sponsorChartRef.current) {
      try {
        const finalY = doc.lastAutoTable.finalY || 72;
        const chartImage = sponsorChartRef.current.toBase64Image();
        
        // Check if we need a new page
        if (finalY + 80 > doc.internal.pageSize.getHeight() - 20) {
          doc.addPage();
          doc.addImage(chartImage, 'PNG', 14, 20, 180, 70);
        } else {
          doc.addImage(chartImage, 'PNG', 14, finalY + 10, 180, 70);
        }
      } catch (err) {
        console.log('Chart export not available:', err);
      }
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc.save(`Sponsor_Purchase_Report_${sponsorPurchases.period.replace('/', '-')}.pdf`);
    toast.success('PDF exported successfully!');
  };

  const exportSponsorDataToExcel = () => {
    if (!sponsorPurchases || !sponsorPurchases.data || sponsorPurchases.data.length === 0) {
      toast.warning('No sponsor data to export');
      return;
    }

    const totalOrders = sponsorPurchases.data.reduce((sum, s) => sum + parseInt(s.purchase_orders || 0), 0);
    const totalItems = sponsorPurchases.data.reduce((sum, s) => sum + parseInt(s.unique_items || 0), 0);
    const totalQty = sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_quantity || 0), 0);
    const totalAmount = sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_purchase_amount || 0), 0);

    // Create header data
    const headerData = [
      ['SPONSOR PURCHASE REPORT'],
      [`Period: ${sponsorPurchases.period}`],
      [`Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`],
      [],
      ['SUMMARY'],
      [`Total Sponsors: ${sponsorPurchases.count}`],
      [`Total Purchase Orders: ${totalOrders}`],
      [`Total Unique Items: ${totalItems}`],
      [`Total Quantity: ${totalQty.toFixed(2)}`],
      [`Total Purchase Amount: ฿${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
      [],
      ['DETAILED DATA']
    ];

    // Table headers
    const tableHeaders = [['#', 'Sponsor Name', 'Purchase Orders', 'Unique Items', 'Total Quantity', 'Total Purchase Amount (฿)']];

    // Table data
    const tableData = sponsorPurchases.data.map((sponsor, index) => [
      index + 1,
      sponsor.sponsor_name,
      sponsor.purchase_orders,
      sponsor.unique_items,
      parseFloat(sponsor.total_quantity || 0).toFixed(2),
      parseFloat(sponsor.total_purchase_amount || 0).toFixed(2)
    ]);

    // Total row
    const totalRow = [
      'TOTAL',
      '',
      totalOrders,
      totalItems,
      totalQty.toFixed(2),
      totalAmount.toFixed(2)
    ];

    // Combine all data
    const worksheetData = [...headerData, ...tableHeaders, ...tableData, totalRow];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 },   // #
      { wch: 30 },  // Sponsor Name
      { wch: 18 },  // Purchase Orders
      { wch: 15 },  // Unique Items
      { wch: 18 },  // Total Quantity
      { wch: 25 }   // Total Purchase Amount
    ];

    // Merge cells for header
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // Title
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }, // Period
      { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } }  // Generated
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sponsor Purchases');

    // Export
    XLSX.writeFile(workbook, `Sponsor_Purchase_Report_${sponsorPurchases.period.replace('/', '-')}.xlsx`);
    toast.success('Excel exported successfully!');
  };

  if (accessDenied) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', marginTop: '50px' }}>
        <Card>
          <h2>Access Denied</h2>
          <p>This dashboard is only available for warehouse store manager accounts.</p>
          <p>Your current role: <strong>{userType || 'Unknown'}</strong></p>
          <Button type="primary" onClick={handleLogout}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <Layout>
      <>
      <ToastContainer />
      
      {/* Floating Inventory Menu Button */}
      <Button
        type="primary"
        icon={<MenuOutlined />}
        shape="circle"
        size="large"
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 100,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          backgroundColor: '#8B4513',
          borderColor: '#8B4513'
        }}
        onClick={() => setInventoryMenuVisible(true)}
        title="Inventory Menu"
      />
      
      {/* Floating Logout Button */}
      <Button
        type="primary"
        icon={<LogoutOutlined />}
        shape="circle"
        size="large"
        danger
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 24,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000
        }}
        onClick={handleLogout}
        title="Logout"
      />

      {/* Inventory Menu Modal */}
      <Modal
        title="Inventory Management"
        open={inventoryMenuVisible}
        onCancel={() => setInventoryMenuVisible(false)}
        footer={null}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Button
            type="primary"
            size="large"
            icon={<SwapOutlined />}
            block
            style={{ height: '60px', fontSize: '16px', backgroundColor: '#8B4513', borderColor: '#8B4513' }}
            onClick={() => {
              navigate('/inventory/stockinout');
              setInventoryMenuVisible(false);
            }}
          >
            Stock In/Out
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<BarChartOutlined />}
            block
            style={{ height: '60px', fontSize: '16px', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => {
              navigate('/inventory/stockreports');
              setInventoryMenuVisible(false);
            }}
          >
            Stock Reports
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<InboxOutlined />}
            block
            style={{ height: '60px', fontSize: '16px', backgroundColor: '#1890ff', borderColor: '#1890ff' }}
            onClick={() => {
              navigate('/inventory/closing-stock');
              setInventoryMenuVisible(false);
            }}
          >
            Closing Stock
          </Button>
        </Space>
      </Modal>

      <div style={{ padding: '0', minHeight: '100vh', background: '#f5f5f5', width: '100%' }}>
        {/* Header Section */}
        <div style={{ backgroundColor: '#8B4513', color: '#fff', padding: '30px 20px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
          <Row gutter={[16, 16]} style={{ width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
            <Col xs={24} sm={12}>
              <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#fff' }}>
                <InboxOutlined style={{ marginRight: '10px' }} />
                Warehouse Store Manager Dashboard
              </h1>
              <p style={{ margin: 0, fontSize: '14px', color: '#fff' }}>
                Manage and monitor your warehouse inventory
              </p>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Space>
                <ShoppingCartOutlined style={{ fontSize: '24px' }} />
                <span style={{ fontSize: '16px' }}>Welcome, {userName}</span>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Main Content */}
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
          {/* Date Range Selector */}
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col xs={24}>
              <Card>
                <Space>
                  <span style={{ fontWeight: 'bold' }}>Select Date Range:</span>
                  <DatePicker.RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    style={{ width: 300 }}
                  />
                  <Button type="primary" onClick={fetchDashboardData} loading={loading}>
                    Refresh Data
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <Spin size="large" tip="Loading dashboard data..." />
            </div>
          ) : (
            <>
              {/* Summary Statistics Cards */}
              <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #8B4513' }}>
                    <Statistic
                      title="Total Stock Value"
                      value={dashboardData?.total_stock_value || 0}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#8B4513', fontSize: '24px', fontWeight: 'bold' }}
                      precision={2}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #52c41a' }}>
                    <Statistic
                      title="Total Items"
                      value={dashboardData?.total_items || 0}
                      prefix={<InboxOutlined />}
                      valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #1890ff' }}>
                    <Statistic
                      title="Total Categories"
                      value={dashboardData?.total_categories || 0}
                      prefix={<AppstoreOutlined />}
                      valueStyle={{ color: '#1890ff', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #faad14' }}>
                    <Statistic
                      title="Total Suppliers"
                      value={dashboardData?.total_suppliers || 0}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: '#faad14', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #722ed1' }}>
                    <Statistic
                      title="Transactions"
                      value={dashboardData?.recent_transactions || 0}
                      prefix={<SwapOutlined />}
                      valueStyle={{ color: '#722ed1', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #f5222d' }}>
                    <Statistic
                      title="Low Stock Items"
                      value={dashboardData?.low_stock_items || 0}
                      prefix={<WarningOutlined />}
                      valueStyle={{ color: '#f5222d', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Charts Row 1 */}
              <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                <Col xs={24} lg={16}>
                  <Card title="Category-wise Stock Analysis" variant="borderless">
                    {getCategoryChartData() ? (
                      <Bar 
                        data={getCategoryChartData()} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: { position: 'top' },
                            title: { display: false }
                          },
                          scales: {
                            y: { beginAtZero: true }
                          }
                        }}
                        height={80}
                      />
                    ) : (
                      <Alert message="No category data available for the selected period" type="info" />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  <Card title="Stock Distribution" variant="borderless">
                    {getStockDistributionData() ? (
                      <Doughnut 
                        data={getStockDistributionData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: { position: 'bottom' }
                          }
                        }}
                      />
                    ) : (
                      <Alert message="No distribution data available" type="info" />
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Charts Row 2 */}
              <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                <Col xs={24}>
                  <Card title="Daily Stock Movement Trends" variant="borderless">
                    {getDailyMovementChartData() ? (
                      <Line 
                        data={getDailyMovementChartData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: { position: 'top' },
                            title: { display: false }
                          },
                          scales: {
                            y: { beginAtZero: true }
                          }
                        }}
                        height={60}
                      />
                    ) : (
                      <Alert message="No daily movement data available for the selected period" type="info" />
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Quick Actions */}
              <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                <Col xs={24}>
                  <Card title="Quick Actions" variant="borderless">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Button
                          type="primary"
                          size="large"
                          icon={<FileTextOutlined />}
                          onClick={() => navigate('/dashboard/warehousestoremanager/report')}
                          block
                          style={{ backgroundColor: '#8B4513', borderColor: '#8B4513' }}
                        >
                          Generate Report
                        </Button>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Button
                          type="default"
                          size="large"
                          icon={<BarChartOutlined />}
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          block
                        >
                          View Analytics
                        </Button>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Button
                          type="default"
                          size="large"
                          icon={<InboxOutlined />}
                          onClick={() => setInventoryMenuVisible(true)}
                          block
                        >
                          Check Inventory
                        </Button>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Button
                          type="default"
                          size="large"
                          icon={<HomeOutlined />}
                          onClick={() => navigate('/dashboard/warehousestoremanager')}
                          block
                        >
                          Home
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* Category Summary Table */}
              {categoryData && categoryData.length > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                  <Col xs={24}>
                    <Card title="Category Summary Details" variant="borderless">
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#8B4513', color: '#fff' }}>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Category</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Unique Items</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Total Received</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Total Sold</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Current Stock</th>
                              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Purchase Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryData.map((cat, index) => (
                              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                                <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{cat.category}</td>
                                <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>{cat.unique_items}</td>
                                <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>{cat.total_received}</td>
                                <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>{cat.total_sold}</td>
                                <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{cat.current_stock}</td>
                                <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                                  ${parseFloat(cat.total_purchase_amount || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Sponsor Purchase Records Section */}
              <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                <Col xs={24}>
                  <Card 
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <span>
                          <TeamOutlined style={{ marginRight: '8px' }} />
                          Sponsor Purchase Records
                        </span>
                        <Space wrap>
                          <span style={{ fontSize: '14px', fontWeight: 'normal' }}>Select Month:</span>
                          <DatePicker
                            picker="month"
                            value={selectedMonth}
                            onChange={(date) => setSelectedMonth(date || dayjs())}
                            format="MMMM YYYY"
                            style={{ width: 200 }}
                          />
                          <Button type="primary" onClick={fetchSponsorPurchases} loading={loadingSponsor}>
                            Refresh
                          </Button>
                          {sponsorPurchases && sponsorPurchases.data && sponsorPurchases.data.length > 0 && (
                            <>
                              <Button
                                type="default"
                                icon={<FilePdfOutlined />}
                                onClick={exportSponsorDataToPDF}
                                style={{ color: '#d32f2f', borderColor: '#d32f2f' }}
                              >
                                Export PDF
                              </Button>
                              <Button
                                type="default"
                                icon={<FileExcelOutlined />}
                                onClick={exportSponsorDataToExcel}
                                style={{ color: '#217346', borderColor: '#217346' }}
                              >
                                Export Excel
                              </Button>
                            </>
                          )}
                        </Space>
                      </div>
                    }
                    variant="borderless"
                  >
                    {loadingSponsor ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" tip="Loading sponsor purchase data..." />
                      </div>
                    ) : (
                      <>
                        {sponsorPurchases && sponsorPurchases.data && sponsorPurchases.data.length > 0 ? (
                          <>
                            {/* Summary Info */}
                            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f2f5', borderRadius: '8px' }}>
                              <Row gutter={16}>
                                <Col xs={24} sm={8}>
                                  <Statistic
                                    title="Period"
                                    value={sponsorPurchases.period}
                                    valueStyle={{ fontSize: '18px', color: '#8B4513' }}
                                  />
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Statistic
                                    title="Total Sponsors"
                                    value={sponsorPurchases.count}
                                    prefix={<TeamOutlined />}
                                    valueStyle={{ fontSize: '18px', color: '#52c41a' }}
                                  />
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Statistic
                                    title="Total Purchase Amount"
                                    value={sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_purchase_amount || 0), 0).toFixed(2)}
                                    prefix="฿"
                                    valueStyle={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }}
                                  />
                                </Col>
                              </Row>
                            </div>

                            {/* Chart */}
                            <div style={{ marginBottom: '20px' }}>
                              {getSponsorPurchaseChartData() && (
                                <Bar 
                                  ref={sponsorChartRef}
                                  data={getSponsorPurchaseChartData()} 
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: true,
                                    plugins: {
                                      legend: { position: 'top' },
                                      title: { 
                                        display: true, 
                                        text: `Sponsor Purchase Analysis - ${sponsorPurchases.period}`,
                                        font: { size: 16, weight: 'bold' }
                                      }
                                    },
                                    scales: {
                                      y: { 
                                        beginAtZero: true,
                                        ticks: {
                                          callback: function(value) {
                                            return '฿' + value.toLocaleString();
                                          }
                                        }
                                      }
                                    }
                                  }}
                                  height={80}
                                />
                              )}
                            </div>

                            {/* Detailed Table */}
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#8B4513', color: '#fff' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Sponsor Name</th>
                                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Purchase Orders</th>
                                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Unique Items</th>
                                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Total Quantity</th>
                                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Total Purchase Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sponsorPurchases.data.map((sponsor, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                                      <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{index + 1}</td>
                                      <td style={{ padding: '10px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>
                                        {sponsor.sponsor_name}
                                      </td>
                                      <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                                        {sponsor.purchase_orders}
                                      </td>
                                      <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                                        {sponsor.unique_items}
                                      </td>
                                      <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                                        {parseFloat(sponsor.total_quantity || 0).toFixed(2)}
                                      </td>
                                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd', fontWeight: 'bold', color: '#1890ff' }}>
                                        ฿{parseFloat(sponsor.total_purchase_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                  ))}
                                  {/* Total Row */}
                                  <tr style={{ backgroundColor: '#e6f7ff', fontWeight: 'bold' }}>
                                    <td colSpan="2" style={{ padding: '12px', borderTop: '2px solid #8B4513', textAlign: 'right' }}>
                                      TOTAL:
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', borderTop: '2px solid #8B4513' }}>
                                      {sponsorPurchases.data.reduce((sum, s) => sum + parseInt(s.purchase_orders || 0), 0)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', borderTop: '2px solid #8B4513' }}>
                                      {sponsorPurchases.data.reduce((sum, s) => sum + parseInt(s.unique_items || 0), 0)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', borderTop: '2px solid #8B4513' }}>
                                      {sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_quantity || 0), 0).toFixed(2)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderTop: '2px solid #8B4513', color: '#1890ff', fontSize: '16px' }}>
                                      ฿{sponsorPurchases.data.reduce((sum, s) => sum + parseFloat(s.total_purchase_amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : (
                          <Alert 
                            message="No sponsor purchase data available" 
                            description={`No purchase records found for ${selectedMonth.format('MMMM YYYY')}`}
                            type="info" 
                            showIcon
                          />
                        )}
                      </>
                    )}
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>
      </>
    </Layout>
  );
};

export default WarehouseStoreManagerDashboard;
